import { takeEvery, select, put, call, all, spawn, take } from 'redux-saga/effects';
import { matchPath } from 'react-router';
import qs from 'qs';

import { LOCATION_CHANGE } from 'connected-react-router';
import { validateProfilingConsent, appendGtagLibraryScript, webStorage } from 'utils';
import {
  application as applicationActions,
  viewer as viewerActions,
  tracking as trackingActions,
  ui as uiActions,
  property as propertyActions,
  tags as tagsActions,
  stores as storesActions,
  flyers as flyersActions,
  flyerGibs as flyerGibsActions,
  products as productsActions,
  brands as brandsActions,
  user as usersActions,
  retailerCart as retailerCartActions,
} from 'flux/actions';
import {
  property as propertySelectors,
  user as userSelectors,
  router as routerSelectors,
  flyers as flyersSelectors,
  flyerGibs as flyerGibsSelectors,
  tags as tagsSelectors,
  viewer as viewerSelectors,
  tracking as trackingSelectors,
  products as productsSelectors,
  brands as brandsSelectors,
} from 'flux/selectors';

import { selectPropertyGoogleAdWordsPhoneCta } from 'flux/selectors/property.selectors';
import { flyerPath, tagPath, tagsPath, storesPath, allPaths } from 'app/routes';

import errorHandler from 'errorHandler';

const pagesDiff = (previousPages, nextPages) =>
  /* eslint-disable no-bitwise */
  nextPages.filter((page) => !~previousPages.indexOf(page));
/* eslint-enable */

export function* trackPagesWithSplunk(pages, data) {
  yield all(pages.map((page) => put(trackingActions.trackSplunk({ ...data, fp: page }))));
}

export function* trackEnFlyerOpen(pages, data) {
  const isProfilingConsent = yield select(propertySelectors.selectIsProfilingConsent);
  if (isProfilingConsent) {
    yield put(
      trackingActions.gtag('event', 'EnFlyerOpen', {
        send_to: 'AW-812359052',
        en_flyer_id: data.fid,
        country: 'it',
        source: 'dvc-marketplace',
      })
    );
  }
}

export function* trackGoogleConversionPDF() {
  const GOOGLE_CONVERSION_ID = 'AW-812326464';
  const GOOGLE_CONVERSION_LABEL = 'z-2mCNS7kogBEMC8rIMD';
  const isProfilingConsent = yield select(propertySelectors.selectIsProfilingConsent);
  const isFromExtendedNetwork = yield select(propertySelectors.isFromExtendedNetwork);
  if (isProfilingConsent && isFromExtendedNetwork) {
    yield put(
      trackingActions.gtag('event', 'conversion', {
        send_to: `${GOOGLE_CONVERSION_ID}/${GOOGLE_CONVERSION_LABEL}`,
      })
    );
  }
}

export function* trackPagesWithFb(pages, flyerId) {
  const flyerRetailer = yield select(flyersSelectors.selectFlyerRetailer, flyerId);
  yield all(
    pages.map((page) =>
      put(
        trackingActions.fbCustom('FlyerPageBrowse', {
          flyer_id: flyerId,
          flyer_page: page,
          retailer_category: flyerRetailer?.category?.slug,
          retailer_slug: flyerRetailer?.slug,
        })
      )
    )
  );
}

function* trackPageView(pages, flyerId) {
  const data = {
    fid: flyerId,
    fvk: 'm',
  };

  yield put(trackingActions.retailerDataLayerPageView(pages));
  yield spawn(trackPagesWithSplunk, pages, data);
  yield spawn(trackPagesWithFb, pages, flyerId);
}

const { INITIALIZE, INITIALIZE_SA, WINDOW_RESIZE_CHANGED_PAGES, BROWSE, EXIT } = viewerActions.types;
export const viewerEvents = {
  [INITIALIZE_SA]: function* initializeEventSA(previousState, nextState) {
    const { flyerId } = nextState;
    const pages = pagesDiff(previousState.currentPages, nextState.currentPages);
    const flyerOpenData = {
      fid: flyerId,
      fvk: 'w',
      a: 'ov',
    };

    yield spawn(trackPagesWithSplunk, pages, flyerOpenData);
  },
  [INITIALIZE]: function* initializeEvent(previousState, nextState) {
    const { flyerId } = nextState;
    const pages = pagesDiff(previousState.currentPages, nextState.currentPages);
    const flyerOpenData = {
      fid: flyerId,
      fvk: 's',
    };
    const pageViewData = {
      fid: flyerId,
      fvk: 'm',
    };
    yield spawn(trackPagesWithSplunk, pages, flyerOpenData);
    yield spawn(trackPagesWithSplunk, pages, pageViewData);
    yield spawn(trackEnFlyerOpen, pages, pageViewData);
    yield spawn(trackGoogleConversionPDF);
  },
  [WINDOW_RESIZE_CHANGED_PAGES]: function* windowResizeChangedPagesEvent(previousState, nextState) {
    yield spawn(trackPageView, nextState.currentPages, nextState.flyerId);
  },
  [BROWSE]: function* browseEvent(previousState, nextState) {
    const { flyerId } = nextState;
    const pages = pagesDiff(previousState.currentPages, nextState.currentPages);

    yield spawn(trackPageView, pages, flyerId);
  },
  [EXIT]: function* exitEvent(previousState) {
    const { flyerId } = previousState;
    const data = {
      fid: flyerId,
      fvk: 'e',
    };
    yield spawn(trackPagesWithSplunk, previousState.currentPages, data);
  },
};

export function* onViewerUpdated({ payload: { eventType, previousState, nextState } }) {
  const isCurrentFlyerSAType = yield select(flyersSelectors.isCurrentFlyerSAType);
  const isFlyerPath = yield select(routerSelectors.routerMatchPath, flyerPath);
  if (isCurrentFlyerSAType) {
    const mappedEvent = viewerEvents[INITIALIZE_SA];
    if (isFlyerPath) {
      yield spawn(mappedEvent, previousState, nextState);
    }
  } else {
    // forward viewerEvents
    const mappedEvent = viewerEvents[eventType];
    if (isFlyerPath && mappedEvent) {
      yield spawn(mappedEvent, previousState, nextState);
    }
  }

  // calculate browsePercentages
  const { viewedPages, flyerId } = nextState;
  const numberOfPages = yield select(viewerSelectors.selectViewerNumberOfPages);
  const percentage = Math.floor((100 * viewedPages.length) / numberOfPages);

  // check and put hitViewerPercentage actions
  const viewerPercentages = yield select(trackingSelectors.selectTrackingViewerPercentages);

  for (const percentageGoal of [30, 50, 80]) {
    if (flyerId in viewerPercentages) {
      const isReached = viewerPercentages[flyerId][percentageGoal];
      if (percentage >= percentageGoal && !isReached) {
        yield put(trackingActions.hitViewerPercentage(flyerId, percentageGoal));
      }
    } else if (percentage >= percentageGoal) {
      yield put(trackingActions.hitViewerPercentage(flyerId, percentageGoal));
    }
  }
}

export function* onViewerInitialize({ payload: { flyerId, currentPages } }) {
  const isSAType = yield select(flyersSelectors.isCurrentFlyerSAType);
  if (isSAType) return;

  yield put(trackingActions.retailerDataLayerOpenAndView(currentPages));

  const { id: propertyId } = yield select(propertySelectors.selectProperty);
  yield put(
    trackingActions.fbEvent('ViewContent', {
      content_type: 'product',
      content_ids: [flyerId],
      content: {
        property_id: propertyId,
      },
    })
  );
}

export function* trackFlyerGibOnFacebook(flyerGib, tag) {
  const {
    settings: {
      price_extended: { currency_code: currency, digits: value },
    },
  } = flyerGib;
  const { id: propertyId } = yield select(propertySelectors.selectProperty);

  const flyer = yield select(flyersSelectors.selectFlyerFromListOrCurrentFlyer, tag.flyerId);

  const {
    retailer: {
      slug: retailerSlug,
      category: { slug: retailerCategory },
    },
  } = flyer;

  const { pathname: previousPathname } = yield select(routerSelectors.selectRouterPreviousLocation);

  const sourcePathRules = {
    [flyerPath]: 'flyer',
    [tagsPath]: 'grid',
    [tagPath]: 'product',
  };

  const source = Object.keys(sourcePathRules).reduce((acc, path) => (matchPath(previousPathname, { path }) ? sourcePathRules[path] : acc), 'direct');

  // FB Pixels
  yield put(
    trackingActions.fbEvent('AddToCart', {
      content_type: 'product',
      content_ids: [flyerGib.id],
      value,
      currency,
      content_name: tag.title,
      content_category: tag.category,
      content: {
        property_id: propertyId,
        flyer_id: tag.flyerId,
        retailer_slug: retailerSlug,
        retailer_category: retailerCategory,
        product_id: flyerGib.productId,
        product_name: tag.title,
        product_brand: tag.brand.name,
        product_category: tag.category,
        source,
      },
    })
  );
}

export function* trackFlyerGibOnGtag(flyerGib, tag) {
  const { pathname: previousPathname } = yield select(routerSelectors.selectRouterPreviousLocation);

  const sourcePathRules = {
    [flyerPath]: 'View from Flyer',
    [tagsPath]: 'View from Grid',
    [tagPath]: 'View from Product',
  };

  const source = Object.keys(sourcePathRules).reduce((acc, path) => (matchPath(previousPathname, { path }) ? sourcePathRules[path] : acc), 'View');

  const brandName = tag.brand ? `${tag.brand.name} | ` : '';
  yield put(
    trackingActions.gtag('event', source, {
      event_category: 'Product',
      event_label: `${flyerGib.id} | ${tag.title} | ${brandName}${tag.category}`,
    })
  );
}

export function* trackFlyerGibStreamFully(flyerGib) {
  const { id, flyer_id: flyerId } = flyerGib;
  yield put(
    trackingActions.trackStreamfully([
      {
        event_type: 'fg',
        fgid: id,
        fid: flyerId,
      },
    ])
  );
}

export function* trackFlyerGib(flyerGibId) {
  // since this tracking depends on flyerGibs.json fetch, we need to wait if not done yet
  const flyerGibFetched = yield select(flyerGibsSelectors.selectFlyerGibsFetched);
  if (!flyerGibFetched) yield take(flyerGibsActions.types.RESET);

  const flyerGib = yield select(flyerGibsSelectors.selectFlyerGib, flyerGibId);
  if (flyerGib) {
    yield spawn(trackFlyerGibStreamFully, flyerGib);

    /*
    trackFlyerGibOnFacebook and trackFlyerGibOnGtag
    depends on "brands.json" and "products.json" fetch,
    so to avoid losing tracking, we need to wait then to be done
    */
    const dependsOn = [];

    const productsFetched = yield select(productsSelectors.selectProductsFetched);
    if (!productsFetched) dependsOn.push(take(productsActions.types.RESET));

    const brandsFetched = yield select(brandsSelectors.selectBrandsFetched);
    if (!brandsFetched) dependsOn.push(take(brandsActions.types.RESET));

    if (dependsOn.length) yield all(dependsOn);

    const tag = yield select(tagsSelectors.selectTag, flyerGibId);
    if (flyerGib.productId && tag) {
      yield spawn(trackFlyerGibOnFacebook, flyerGib, tag);
      yield spawn(trackFlyerGibOnGtag, flyerGib, tag);
    }
  }
}

function* sendFlyerGibButton({ buttonName, buttonText, buttonUrl, tagId, flyerId, storeId }) {
  const phonePropertyGAWLabel = yield select(selectPropertyGoogleAdWordsPhoneCta);
  if (buttonName === 'btn_flyer_call') {
    yield put(
      trackingActions.fbCustom('FlyerGibsCallCta', {
        flyer_id: flyerId,
        flyer_gib_id: tagId,
        store_id: storeId,
      })
    );

    // todo suppongo che l'id gaw rimanga invariato e cambi solo la label
    yield put(trackingActions.gawConversion(phonePropertyGAWLabel));
  }

  yield put(
    trackingActions.trackStreamfully([
      {
        event_type: 'fgb',
        fgid: tagId,
        fid: flyerId,
        url: buttonUrl,
        text: buttonText,
      },
    ])
  );
}

export function* trackFlyerGibButton({ payload: { button, tagId, flyerId, storeId } }) {
  yield spawn(sendFlyerGibButton, {
    buttonName: button.name,
    buttonText: button.value,
    buttonUrl: button.attributes.href,
    tagId,
    flyerId,
    storeId,
  });
}

export function* trackAddToCartCtaClick({ payload: { tagId } }) {
  const buttonText = yield select(propertySelectors.selectAddToCartCtaText);
  const flyerId = yield select(flyersSelectors.selectCurrentFlyerId);

  yield spawn(sendFlyerGibButton, {
    buttonName: 'add_to_cart',
    buttonText,
    buttonUrl: '',
    tagId,
    flyerId,
    storeId: undefined,
  });
}

export function* onViewerEnrichment({ payload: { id } }) {
  const flyerGib = yield select(flyerGibsSelectors.selectFlyerGib, id);

  if (flyerGib) {
    if (flyerGib.type === 'external_link') {
      yield put(trackingActions.retailerDataLayerClickoutLinkClicked(id));
      yield spawn(trackFlyerGib, id);
    }
  }
}

export function* trackStoresListOpened(flyerId) {
  const filteredCurrentFlyerNearestStore = yield select(flyersSelectors.filteredCurrentFlyerNearestStore);
  const storeAdress = filteredCurrentFlyerNearestStore?.address;
  const { origin: geolocationOrigin } = yield select(userSelectors.selectNearbyGeolocation);

  yield put(
    trackingActions.gtag('event', 'Stores list opened', {
      event_category: 'Stores List',
      event_label: `${flyerId} | ${storeAdress} | ${geolocationOrigin}`,
    })
  );
}

export function* onRouterLocationChange({ payload: { location, isFirstRendering } }) {
  const { pathname } = location;

  const isInvalidPath = allPaths.every((path) => !matchPath(pathname, path));
  if (!isInvalidPath) {
    yield put(trackingActions.gaPageview(location));

    const matchTagPath = matchPath(pathname, { path: tagPath });
    if (matchTagPath) {
      const {
        params: { tagId },
      } = matchTagPath;
      yield spawn(trackFlyerGib, tagId);
    }

    const matchStoresPath = matchPath(pathname, { path: storesPath });
    if (matchStoresPath) {
      const {
        params: { flyerId },
      } = matchStoresPath;
      if (isFirstRendering) yield take(flyersActions.types.SET_CURRENT_FLYER_STORES);
      yield spawn(trackStoresListOpened, flyerId);
    }
  }
}

export function* onSetSelectedProductVariantIndex({ payload: { activeParentTagId, selectedVariantIndex } }) {
  if (typeof selectedVariantIndex !== 'number') return;

  const { selectedTagId } = yield select(tagsSelectors.selectProductVariantIdsInfo, activeParentTagId);

  yield spawn(trackFlyerGib, selectedTagId);
}

export function onGtag({ payload: { args } }) {
  window.gtag(...args);
}

export function* onApplicationInit() {
  const propertyGAIds = yield select(propertySelectors.selectPropertyGoogleAnalytics);

  // get profiling_consent from queryString
  const queryString = window.location.search;
  let { profilingConsent: PCParam } = qs.parse(queryString, {
    ignoreQueryPrefix: true,
  });
  PCParam = validateProfilingConsent(PCParam);

  // get Default profiling_consent from property
  let PCSelector = yield select(propertySelectors.selectIsProfilingConsent);
  const { id: propertyId } = yield select(propertySelectors.selectProperty);
  PCSelector = validateProfilingConsent(PCSelector) || false;

  // get Default profiling_consent from localStorage
  const PCMapping = JSON.parse(webStorage.getItem('profilingConsent')) || {};
  const PCStorage = PCMapping[propertyId];

  // profiling_consent
  let profilingConsent;
  if (PCParam !== null) {
    profilingConsent = PCParam;
  } else {
    profilingConsent = PCStorage !== undefined ? PCStorage : PCSelector;
  }

  if (profilingConsent !== null || profilingConsent !== undefined) {
    yield put(trackingActions.profileConsentInit(profilingConsent));

    // TODO: these two are used together at all times, to they could be simplified to avoid confusion
    yield put(propertyActions.toggleProfilingConsent(profilingConsent));
    yield put(propertyActions.setProfilingConsent(profilingConsent));
  }

  // Google Analytics shopfully
  // gtag script
  try {
    if (propertyGAIds[0]) {
      yield call(appendGtagLibraryScript, propertyGAIds[0]);
    }
  } catch (e) {
    errorHandler(e);
  }
  yield put(trackingActions.gtag('js', new Date()));
  if (propertyGAIds[0]) {
    yield put(
      trackingActions.gtag('config', propertyGAIds[0], {
        send_page_view: false,
        anonymize_ip: true,
        cookie_flags: 'secure;samesite=none',
      })
    );
    yield put(
      trackingActions.gtag('consent', 'default', {
        analytics_storage: 'granted',

        ad_storage: profilingConsent ? 'granted' : 'denied',
        ad_user_data: profilingConsent ? 'granted' : 'denied',
        ad_personalization: profilingConsent ? 'granted' : 'denied',

        functionality_storage: 'denied',
        security_storage: 'denied',
        personalization_storage: 'denied',
      })
    );
  }
}

export function* onConsentTracking({ payload: { profilingConsent } }) {
  const validatedProfilingConsent = validateProfilingConsent(profilingConsent);
  if (!validatedProfilingConsent) {
    if (window.fbq) window.fbq('consent', 'revoke');
    yield put(
      trackingActions.gtag('consent', 'update', {
        analytics_storage: 'granted',

        ad_storage: 'denied',
        ad_user_data: 'denied',
        ad_personalization: 'denied',

        functionality_storage: 'denied',
        security_storage: 'denied',
        personalization_storage: 'denied',
      })
    );
    return;
  }

  const propertyPixels = yield select(propertySelectors.selectPropertyFBPixels);
  const propertyGAIds = yield select(propertySelectors.selectPropertyGoogleAnalytics);
  const propertyGAWId = yield select(propertySelectors.selectPropertyGoogleAdWords);

  // fbPixel
  yield put(trackingActions.fbQ('consent', 'grant'));
  for (const pixel of propertyPixels) {
    yield put(trackingActions.fbInit(pixel));
  }

  // gtag script
  try {
    yield call(appendGtagLibraryScript, propertyGAWId);
    yield put(
      trackingActions.gtag('consent', 'update', {
        analytics_storage: 'granted',

        ad_storage: validatedProfilingConsent ? 'granted' : 'denied',
        ad_user_data: validatedProfilingConsent ? 'granted' : 'denied',
        ad_personalization: validatedProfilingConsent ? 'granted' : 'denied',

        functionality_storage: 'denied',
        security_storage: 'denied',
        personalization_storage: 'denied',
      })
    );
  } catch (e) {
    errorHandler(e);
  }

  // Google Analytics Client
  // remove first GAId of shopfully beacuase already fired event cookies not accepted
  propertyGAIds.shift();
  for (const id of propertyGAIds) {
    if (id) yield put(trackingActions.gtag('config', id, { send_page_view: false }));
  }
  // AdWords
  yield put(trackingActions.gtag('config', propertyGAWId, { send_page_view: false }));
}

export function* trackCTA(event) {
  const currentFlyerId = yield select(flyersSelectors.selectCurrentFlyerId);
  const { currentPages } = yield select(viewerSelectors.selectViewer);

  const events = currentPages.map((page) => ({
    event_type: event,
    fid: currentFlyerId,
    fp: page,
  }));

  yield put(trackingActions.trackStreamfully(events));
}

export function* onUiToggleModal({ payload: { active } }) {
  yield spawn(trackCTA, active ? 'fcta_view' : 'fcta_close');
}

export function* onPropertyCTAClick() {
  yield spawn(trackCTA, 'fcta_click');
}

export function* onHitViewerPercentage({ payload: { flyerId, reachedPercentage } }) {
  //selectCurrentFlyerRetailerAssetsObject
  const flyerRetailer = yield select(flyersSelectors.selectFlyerRetailer, flyerId);

  yield put(
    trackingActions.fbCustom('FlyerPageBrowsePercentage', {
      flyer_id: flyerId,
      browse_percentage: reachedPercentage,
      retailer_category: flyerRetailer?.category?.slug,
      retailer_slug: flyerRetailer?.slug,
    })
  );

  const percentageGAWLabel = yield select(propertySelectors.selectPropertyGAWPercentageLabel, reachedPercentage);
  yield put(trackingActions.gawConversion(percentageGAWLabel));
}

export function* onStoreDetailsInteraction({ payload: { interaction, storeAddress } }) {
  const interactionMap = {
    openStoreDetails: 'Opened store details',
    getDirections: 'Asked store directions',
    clickToCall: 'Called a store',
  };

  const action = interactionMap[interaction];
  if (action) {
    const flyerId = yield select(flyersSelectors.selectCurrentFlyerId);
    yield put(
      trackingActions.gtag('event', action, {
        event_category: 'Store Actions',
        event_label: `${flyerId} | ${storeAddress}`,
      })
    );
  }
}

export function* onFlyerGibVideoStart(action) {
  const {
    payload: { fgid },
  } = action;
  const flyerId = yield select(flyersSelectors.selectCurrentFlyerId);
  const events = [
    {
      event_type: 'pv',
      fid: flyerId,
      fgid,
      origin: 'v',
    },
  ];
  yield put(trackingActions.trackStreamfully(events));
}

export function* onFlyerGibVideoStep(action) {
  const {
    payload: { fgid, step, seeked },
  } = action;
  const flyerId = yield select(flyersSelectors.selectCurrentFlyerId);
  const events = [
    {
      event_type: 'vv',
      fid: flyerId,
      fgid,
      pvv: step,
      mode: seeked ? 'ff' : 'noff',
    },
  ];
  yield put(trackingActions.trackStreamfully(events));
}

export function* onNewUserSessionId() {
  const webnetSp = yield select(routerSelectors.selectRouterLocation);
  const events = [
    {
      event_type: 'webnet_se',
      webnet_st: 's',
      webnet_sp: webnetSp.pathname.replace('/', ''),
    },
  ];
  yield put(trackingActions.trackStreamfullyInit(events));
}

export default function* () {
  yield takeEvery(applicationActions.types.INIT, onApplicationInit);
  yield takeEvery(propertyActions.types.TOGGLE_PROFILING_CONSENT, onConsentTracking);
  yield takeEvery(viewerActions.types.INITIALIZE, onViewerInitialize);
  yield takeEvery(viewerActions.types.UPDATED, onViewerUpdated);
  yield takeEvery(viewerActions.types.ENRICHMENT, onViewerEnrichment);
  yield takeEvery(LOCATION_CHANGE, onRouterLocationChange);
  yield takeEvery(tagsActions.types.SET_SELECTED_VARIANT_INDEX, onSetSelectedProductVariantIndex);
  yield takeEvery(trackingActions.types.GTAG, onGtag);
  yield takeEvery(uiActions.types.TOGGLE_MODAL, onUiToggleModal);
  yield takeEvery(propertyActions.types.CTA_CLICK, onPropertyCTAClick);
  yield takeEvery(tagsActions.types.CLICK_BUTTON, trackFlyerGibButton);
  yield takeEvery(retailerCartActions.types.ADD_PRODUCT, trackAddToCartCtaClick);
  yield takeEvery(trackingActions.types.HIT_VIEWER_PERCENTAGE, onHitViewerPercentage);
  yield takeEvery(storesActions.types.DETAILS_INTERACTION, onStoreDetailsInteraction);
  yield takeEvery(tagsActions.types.VIDEO_START, onFlyerGibVideoStart);
  yield takeEvery(tagsActions.types.VIDEO_STEP, onFlyerGibVideoStep);
  yield takeEvery(usersActions.types.SET_SESSION_ID, onNewUserSessionId);
}
